/**
 * FRONT-895: reusable "pick an active project, or start a new one" combobox.
 * Built for the Create-project dialog's project row (the "Change"/"Pick
 * existing" control) but deliberately generic — any future caller that needs
 * the same picker can reuse it.
 *
 * Backed by `useWorkBoard()` rather than `useGraphEntities`: it's the same
 * one-shot, already-cached, unfiltered read the Work board itself uses, and
 * the "client-side name filter" this picker needs is exactly what `Command`'s
 * built-in filtering gives for free — no need for the paginated entities
 * endpoint's server-side `q` param. Filters to ACTIVE (`archivedAt === null`)
 * project containers only, mirroring the Work board's Active view.
 */

import { useMemo, useState } from 'react';
import { CaretDownIcon, CheckIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/components/ui/command';
import { cn } from '~/lib/utils';
import { useWorkBoard, type WorkBoardItemType } from '~/hooks/queries/use-work-board';

export interface ContainerPickerOption {
  id: string;
  name: string;
  type: WorkBoardItemType;
}

export interface ContainerPickerProps {
  /** What to render as the popover's trigger — the "Change" / "Pick existing" control. */
  trigger: React.ReactNode;
  /** Currently selected existing container id, if any — shown checked in the list. */
  value?: string | null;
  onSelectExisting: (option: ContainerPickerOption) => void;
  /**
   * Offer a "New project" row. Omit for a flow (FRONT-1339: moving a note)
   * that must resolve to an EXISTING active container and must never mint
   * one — the row's presence is driven entirely by whether a handler was
   * passed, so there's no separate flag to keep in sync.
   */
  onSelectNew?: () => void;
  /**
   * Label for the "New project" row. FRONT-2111 names the project it would
   * create ("New project — Acme Blog") so the row says what it will do rather
   * than only what kind of thing it makes. Defaults to the generic wording
   * every existing caller already shows.
   */
  newProjectLabel?: string;
  /**
   * Exclude one container id from the option list — e.g. a note's own
   * current container, so a "move" picker never offers a no-op move to
   * itself.
   */
  excludeId?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * FRONT-1738: offer a "Remove from project" row at the bottom of the list
   * — e.g. clearing a chat's Work-container scope entirely, as opposed to
   * moving it to a different one. Omit for a flow with nothing to clear (the
   * row's presence is driven entirely by whether a handler was passed, same
   * convention as `onSelectNew` above) — when absent this component renders
   * and behaves exactly as it did before this prop existed.
   */
  onClear?: () => void;
  /** Label for the row above. Only read when `onClear` is passed. */
  clearLabel?: string;
  /**
   * FRONT-1738: skip the popover's exit animation. Radix's `Presence` keeps
   * a closing `PopoverContent` — `DismissableLayer`, live Escape handling
   * and all — mounted for as long as its CSS exit animation plays (~100ms
   * here); it only unmounts SYNCHRONOUSLY, tearing that layer down
   * immediately, when it detects no animation is running. A caller whose
   * trigger sits inside something ELSE that also listens for Escape at the
   * document level (`chat-scope-chip.tsx`'s hand-rolled chat dialog) can
   * have that outer listener's very next Escape swallowed by this
   * popover's own still-mounted layer instead — purely because it was
   * pressed fast enough to land inside that animation window, which an
   * automated click-then-Escape sequence (or a fast human) easily does.
   * Confirmed live: an immediate Escape right after selecting an option
   * reproduced this 100% of the time without this prop, 0% with it.
   *
   * An inline `animation: 'none'` style on `PopoverContent` does NOT work
   * here — confirmed by reading `@radix-ui/react-popper`'s own `Content`,
   * which spreads `contentProps.style` and then unconditionally sets its
   * OWN `animation` key afterward in the same object literal (`'none'`
   * while unpositioned, `undefined` — no inline rule at all — once
   * positioned), silently discarding whatever this component passes the
   * instant the popover has been positioned once, which by the time
   * anything is closing it, it always has been. So this prop instead gates
   * `PopoverContent` itself: rendered only while `isOpen`, which is a
   * plain REACT unmount with no Presence-driven exit-animation deferral to
   * race — there is nothing left mounted for `DismissableLayer` to keep
   * registered past the same tick `isOpen` goes false. Only the CLOSE side
   * is affected; the open transition (and its animate-in) is untouched.
   * Omit for the normal, animated close — `note-row.tsx`'s Move control
   * does not pass this and is unaffected.
   */
  instantClose?: boolean;
}

export function ContainerPicker({
  trigger,
  value,
  onSelectExisting,
  onSelectNew,
  newProjectLabel,
  excludeId,
  open,
  onOpenChange,
  onClear,
  clearLabel,
  instantClose,
}: ContainerPickerProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = open ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;
  // FRONT-1339 code-review: don't fetch the whole board until the picker is
  // actually opened — see useWorkBoard's docblock. The trigger itself stays
  // cheap and always rendered, so the hover affordance is unaffected.
  const { items } = useWorkBoard({ enabled: isOpen });

  const options: ContainerPickerOption[] = useMemo(
    () =>
      items
        .filter((item) => item.archivedAt === null && item.id !== excludeId)
        .map((item) => ({ id: item.id, name: item.name, type: item.type })),
    [items, excludeId],
  );

  // FRONT-1738: when `instantClose` is set, only render `PopoverContent` at
  // all while `isOpen` — see the prop's own docblock. This is a REACT-level
  // unmount, not a CSS one: an inline `animation: 'none'` on `PopoverContent`
  // does NOT work here — `@radix-ui/react-popper`'s own `Content` spreads
  // `contentProps.style` and then unconditionally sets its OWN `animation`
  // key afterward (`none` while unpositioned, `undefined` — i.e. "no inline
  // rule at all" — once positioned), so any inline value this component
  // passes is silently overwritten the instant the popover has been
  // positioned once, which by the time anyone is closing it, it always has
  // been. Gating the whole subtree in React instead sidesteps Radix's
  // Presence-driven exit-animation deferral entirely: there is nothing left
  // for React to keep mounted, so nothing keeps `DismissableLayer`
  // registered past the same tick `isOpen` goes false. The OPEN transition
  // (and its animate-in) is unaffected — this only short-circuits the CLOSE
  // side.
  const showContent = !instantClose || isOpen;

  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      {showContent && (
      <PopoverContent align="start" className="w-72 p-0">
        <Command>
          <CommandInput placeholder="Search projects…" />
          <CommandList className="max-h-64">
            {onSelectNew && (
              <CommandGroup>
                <CommandItem
                  value="__new_project__"
                  onSelect={() => {
                    onSelectNew();
                    setOpen(false);
                  }}
                >
                  <PlusIcon className="size-4" /> {newProjectLabel ?? 'New project'}
                </CommandItem>
              </CommandGroup>
            )}
            <CommandEmpty>No active projects match.</CommandEmpty>
            {options.length > 0 && (
              <CommandGroup heading="Active">
                {options.map((option) => (
                  <CommandItem
                    key={option.id}
                    value={`${option.name} ${option.type}`}
                    onSelect={() => {
                      onSelectExisting(option);
                      setOpen(false);
                    }}
                    className="flex items-center gap-2"
                  >
                    <span className="min-w-0 flex-1 truncate">{option.name}</span>
                    <span className="shrink-0 text-[length:var(--text-2xs)] uppercase text-muted-foreground">
                      {option.type}
                    </span>
                    <CheckIcon
                      className={cn('size-4 shrink-0', option.id === value ? 'opacity-100' : 'opacity-0')}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {onClear && (
              <CommandGroup>
                <CommandItem
                  value="__clear_scope__"
                  onSelect={() => {
                    onClear();
                    setOpen(false);
                  }}
                >
                  <XIcon className="size-4" /> {clearLabel ?? 'Clear'}
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
      )}
    </Popover>
  );
}

/** A ready-made trigger button matching the app's existing combobox chrome
 *  (see `custom-agent-form.tsx`'s model picker) — callers can still pass their
 *  own `trigger` node (e.g. the dialog's "Change"/"Pick existing" Button). */
export function ContainerPickerTrigger({
  label,
  open,
}: {
  label: string;
  open: boolean;
}) {
  return (
    <button
      type="button"
      role="combobox"
      aria-expanded={open}
      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-transparent px-2.5 text-xs font-medium text-foreground hover:bg-accent"
    >
      {label} <CaretDownIcon className="size-3.5 text-muted-foreground" />
    </button>
  );
}
